import { RoleEfficiencySnapshot } from '../../state/RoleEfficiencySnapshot';
import { format, parseISO } from 'date-fns';

/**
 * Данные об эффективности роли за период
 */
export interface RoleEfficiencyData {
    uuid: string;
    name: string;
    color: string;
    averageEfficiency: number | null; // средняя эффективность за период (0-8)
    snapshotsCount: number; // количество снимков за период
}

/**
 * Находит все снимки эффективности за указанный период
 * @param snapshots - все снимки эффективности
 * @param dateFrom - начало периода (YYYY-MM-DD)
 * @param dateTo - конец периода (YYYY-MM-DD)
 */
export function findRoleEfficiencySnapshotsForPeriod(
    snapshots: RoleEfficiencySnapshot[],
    dateFrom: string,
    dateTo: string
): RoleEfficiencySnapshot[] {
    return snapshots.filter(s => {
        const snapshotDate = s.date;
        return snapshotDate >= dateFrom && snapshotDate <= dateTo;
    });
}

/**
 * Вычисляет среднюю эффективность для каждой роли за период
 * @param snapshots - все снимки эффективности
 * @param dateFrom - начало периода (YYYY-MM-DD)
 * @param dateTo - конец периода (YYYY-MM-DD)
 * @param roleNames - карта UUID -> название роли
 * @param roleColors - карта UUID -> цвет роли
 */
export function calculateAverageRoleEfficiency(
    snapshots: RoleEfficiencySnapshot[],
    dateFrom: string,
    dateTo: string,
    roleNames: Map<string, string>,
    roleColors: Map<string, string>
): RoleEfficiencyData[] {
    const periodSnapshots = findRoleEfficiencySnapshotsForPeriod(snapshots, dateFrom, dateTo);
    
    if (periodSnapshots.length === 0) {
        return [];
    }

    // Собираем все уникальные UUID ролей из снимков
    const roleUuids = new Set<string>();
    periodSnapshots.forEach(snapshot => {
        Object.keys(snapshot.scores).forEach(uuid => roleUuids.add(uuid));
    });

    // Для каждой роли считаем среднюю эффективность
    const result: RoleEfficiencyData[] = [];
    
    roleUuids.forEach(uuid => {
        const scores: number[] = [];
        
        periodSnapshots.forEach(snapshot => {
            if (snapshot.scores[uuid] !== undefined) {
                scores.push(snapshot.scores[uuid]);
            }
        });

        const averageEfficiency = scores.length > 0
            ? scores.reduce((sum, score) => sum + score, 0) / scores.length
            : null;

        result.push({
            uuid,
            name: roleNames.get(uuid) || 'Без названия',
            color: roleColors.get(uuid) || '#6c757d',
            averageEfficiency,
            snapshotsCount: scores.length,
        });
    });

    // Сортируем по названию
    result.sort((a, b) => a.name.localeCompare(b.name));

    return result;
}

/**
 * Получает среднюю эффективность для конкретной роли за период
 * @param snapshots - все снимки эффективности
 * @param dateFrom - начало периода (YYYY-MM-DD)
 * @param dateTo - конец периода (YYYY-MM-DD)
 * @param roleUuid - UUID роли
 */
export function getAverageEfficiencyForRole(
    snapshots: RoleEfficiencySnapshot[],
    dateFrom: string,
    dateTo: string,
    roleUuid: string
): number | null {
    const periodSnapshots = findRoleEfficiencySnapshotsForPeriod(snapshots, dateFrom, dateTo);
    
    if (periodSnapshots.length === 0) {
        return null;
    }

    const scores: number[] = [];
    
    periodSnapshots.forEach(snapshot => {
        if (snapshot.scores[roleUuid] !== undefined) {
            scores.push(snapshot.scores[roleUuid]);
        }
    });

    if (scores.length === 0) {
        return null;
    }

    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}
